import { cloneDeep, flatMap, debounce } from 'lodash'
import {
  ApiService,
  DataWrap,
  GeneralSettings,
  GenericOption,
  GeneticTextResult,
  GenericUser,
  NoteType,
  ObservationBody,
  PaginatedGet,
  ReportAnswer,
  ReportCorrectiveAction,
  ReportPhoto,
  ReportQuestion,
  ReportSettings,
  SignatureType,
  UIRouterState,
} from '@/react/types'
import moment from 'moment'
import { Dispatch, SetStateAction } from 'react'

import { Subject, Observable, mergeMap, tap, map, from } from 'rxjs'
import * as RX from 'rxjs'

const PAGE_REQUEST_SIZE = 1000

// Extended UIRouterState interface to include go method
interface ExtendedUIRouterState extends UIRouterState {
  go(state: string, params?: any, options?: any): void
}

// Shared types that can be exported and reused
export interface ApiResponseData<T = any> {
  data: T
}

export interface AnswerWithMetadata extends ReportAnswer {
  edited?: boolean
  updatedAt?: Date
}

export interface PhotoReference {
  id: number
  deleted?: boolean
}

export interface SyncApiAnswer {
  id: number
  updatedAt: Date
  question?: number
  category?: number
  answer?: string
  severity?: number
  reference?: string
  copy_sequence_number?: number
  deleted?: boolean
  party_observed?: any[]
  answer_party_observed_answer_list?: any[]
  observation?: number
  notes?: any[]
  corrective_actions?: any[]
  photos?: any[]
  party_observed_easy_acces_data?: any
}

export interface ProcessingElement {
  answer: SyncApiAnswer
  Api: ApiService
  observationID: number
  copy_sequence_number: number
  date: Date
  resolve: (answer: SyncApiAnswer) => void
}

// Add type guards at the top
function isValidId(value: unknown): value is number {
  return typeof value === 'number' && value > 0
}

function hasValidData<T>(response: unknown): response is { data: T } {
  return typeof response === 'object' && response !== null && 'data' in response
}

// Define proper types for API responses
interface NoteWithUser extends NoteType {
  user?: {
    id: number
  }
}

interface CorrectiveActionWithUser {
  id: number
  text: string
  deleted: boolean
  date_update?: string
  date_created: string
  user?: {
    id: number
  }
}

interface AnswerResponse {
  id: number
  question: number
  category: number
  notes?: ReportCorrectiveAction[]
  corrective_actions?: ReportCorrectiveAction[]
}

// Define proper types for signatures and notes processing
interface RawSignature {
  id: number
  user: any
  deleted: boolean
  text: string
  url: string
  date_created: string
  date_updated: string
  observation: number
  client_object_key?: string
  image?: {
    id: number
    image: string
  }
  printed_name?: string
}

interface RawNote {
  id: number
  text: string
  observation: number
  deleted: boolean
}

// Field type for combineLocalAndDataVariableFields - using existing ReportCorrectiveAction structure
interface ProcessingField {
  text: string
  id: number
  deleted: boolean
  is_visible?: boolean
  fieldmeta?: any
}

// Global observation activation debounce service to prevent multiple redundant requests
class ObservationActivationService {
  private static instance: ObservationActivationService
  private activationPromises: Map<number, Promise<void>> = new Map()
  private debouncedActivations: Map<number, (...args: any[]) => void> =
    new Map()

  static getInstance(): ObservationActivationService {
    if (!ObservationActivationService.instance) {
      ObservationActivationService.instance = new ObservationActivationService()
    }
    return ObservationActivationService.instance
  }

  async activateObservation(
    Api: ApiService,
    observationID: number,
    baseObservationData: ObservationBody,
    allowActivate: boolean,
  ): Promise<void> {
    // Check if there's already an activation in progress for this observation
    const existingPromise = this.activationPromises.get(observationID)
    if (existingPromise) {
      return existingPromise
    }

    // Create a new activation promise
    const activationPromise = this.performActivation(
      Api,
      observationID,
      baseObservationData,
      allowActivate,
    )

    // Store the promise to prevent duplicate requests
    this.activationPromises.set(observationID, activationPromise)

    try {
      await activationPromise
    } finally {
      // Clean up the promise after completion
      this.activationPromises.delete(observationID)
    }
  }

  private async performActivation(
    Api: ApiService,
    observationID: number,
    baseObservationData: ObservationBody,
    allowActivate: boolean,
  ): Promise<void> {
    try {
      // Only make the PATCH request if the title needs to be updated
      await Api.patch(`observations/${observationID}`, {
        title: baseObservationData.title,
        is_active: true,
      })

      // Only make the activation request if the observation is not already active
      if (!baseObservationData.is_active && allowActivate) {
        await Api.put(`observations/${observationID}/activate/`, {})
        baseObservationData.is_active = true
      }
    } catch (error) {
      console.error('Error activating observation:', error)
      throw error
    }
  }

  getDebouncedActivation(observationID: number): (...args: any[]) => void {
    if (!this.debouncedActivations.has(observationID)) {
      const debouncedFn = debounce(
        (
          Api: ApiService,
          baseObservationData: ObservationBody,
          allowActivate: boolean,
        ) => {
          void this.activateObservation(
            Api,
            observationID,
            baseObservationData,
            allowActivate,
          )
        },
        1000, // 1 second debounce for good balance of performance and responsiveness
      )
      this.debouncedActivations.set(observationID, debouncedFn)
    }
    return this.debouncedActivations.get(observationID)!
  }

  clearDebounce(observationID: number): void {
    this.debouncedActivations.delete(observationID)
  }
}

export const fetchData = async (
  Api: ApiService,
  requestParams: {
    deleted: string
    observation: number
    page_size: number
    application: number
  },
  observationID: string | number,
  userId: string | number,
  setPartyObservedOptions: (value: GenericOption[]) => void,
  setBaseObservationData: (value: ObservationBody) => void,
  setQuestionData: (value: ReportQuestion[] | null) => void,
  setNotes: (value: NoteType[]) => void,
  setSignatures: (value: SignatureType[]) => void,
  setMemoryState: (value: { [key: string]: ReportAnswer[] } | null) => void,
  setSettings: (value: {
    generalSettings: GeneralSettings
    reportSettings: ReportSettings
  }) => void,
  ANSWER: ReportAnswer,
  setWildcard: (value: string) => void,
  setWildcardId: (value: number) => void,
  setTitle: (value: string) => void,
  setTitleManuallyEdited: (value: boolean) => void,
) => {
  const [{ data: observationData }, { data: globalPO }, { data: userPO }]: [
    DataWrap<ObservationBody>,
    DataWrap<PaginatedGet<GeneticTextResult[]>>,
    DataWrap<PaginatedGet<GeneticTextResult[]>>,
  ] = (await Promise.all([
    Api.Observations.byIDWithoutDeletedParam(observationID.toString(), () => {
      // Empty callback required by the API
    }),
    Api.get('answer_party_observed', {
      page: 1,
      deleted: false,
      is_active: true,
      page_size: 1000,
      has_user: false,
    }),
    Api.get('answer_party_observed', {
      page: 1,
      deleted: false,
      is_active: true,
      page_size: 1000,
      has_user: true,
      user: parseInt(userId.toString()),
    }),
  ])) as [
    DataWrap<ObservationBody>,
    DataWrap<PaginatedGet<GeneticTextResult[]>>,
    DataWrap<PaginatedGet<GeneticTextResult[]>>,
  ]

  const answers: ReportAnswer[] = []
  const answerPagesToGet: number = Math.ceil(
    observationData.answers_count / PAGE_REQUEST_SIZE,
  )

  const answersPromises: Promise<DataWrap<PaginatedGet<ReportAnswer[]>>>[] = []

  for (let i = 1; i < answerPagesToGet + 1; i++) {
    answersPromises.push(
      Api.Answers.get({
        ...requestParams,
        page_size: PAGE_REQUEST_SIZE,
        page: i,
      }),
    )
  }

  const answersGet: DataWrap<PaginatedGet<ReportAnswer[]>>[] =
    await Promise.all(answersPromises)

  answersGet.forEach((singleAnswerGet) => {
    singleAnswerGet.data.results.forEach((answer) => {
      answers.push(answer)
    })
  })

  const optionsArray: GenericOption[] = []
  const optionIdToOrder: { [key: string | number]: number } = {}

  globalPO.results.forEach((po, idx) => {
    optionsArray.push({ value: po.id, label: po.name, user: null })
    optionIdToOrder[po.id] = idx
  })
  userPO.results.forEach((po, idx) => {
    optionsArray.push({ value: po.id, label: po.name, user: po.user })
    optionIdToOrder[po.id] = optionsArray.length + idx - 1
  })
  setPartyObservedOptions(optionsArray.sort(compare))

  const settingsData = {
    generalSettings: observationData.client.general_settings,
    reportSettings: observationData.client.report_settings,
  }

  if (
    observationData.observation_wildcards &&
    observationData.observation_wildcards.length
  ) {
    observationData.observation_wildcards.forEach((wildcard) => {
      if (!wildcard.deleted) {
        setWildcard(wildcard.text)
        setWildcardId(wildcard.id as number)
      }
    })
  }
  if (observationData.title) {
    setTitle(observationData.title)
    setTitleManuallyEdited(true)
  }
  setBaseObservationData(observationData)
  setQuestionData([]) // Initialize with empty array since questions will be loaded per category
  setNotes(notesProcessing(observationData.observation_notes))
  setSignatures(
    signaturesProcessing(observationData.signatures as RawSignature[]),
  )

  setMemoryState(listProcessing([], answers, Number(observationID), ANSWER))
  setSettings(settingsData)
}

export async function sendForm(
  memoryState: {
    [key: string]: ReportAnswer[]
  } | null,
  Api: ApiService,
  signatures: SignatureType[],
  notes: NoteType[],
  observationID: number,
  stateService: ExtendedUIRouterState,
  setDisableButton: (arg0: boolean) => void,
  wildcard: string,
  wildcardId: number,
  baseObservationData: ObservationBody,
  onAnswerEdit = false,
  titleManuallyEdited?: boolean,
  Notification?: unknown,
  currentUserId?: number,
) {
  if (!memoryState) {
    return
  }

  let continueSaving = true

  flatMap(cloneDeep(memoryState))
    .filter((answer) => answer && typeof answer === 'object')
    .forEach((answer) => {
      if (answer.answer === 'cls') {
        if (
          !checkVariableFieldsForText(
            answer.corrective_actions as ProcessingField[],
          )
        ) {
          const element = document.getElementById(
            `${answer.question}-${answer.category}`,
          )
          if (element) {
            continueSaving = false
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
            return
          }
        }
      }
    })
  if (!continueSaving) return

  if (!onAnswerEdit) {
    await sendAnswers(Api, memoryState, observationID)
  }

  const allowActivate =
    baseObservationData.client.report_settings.wildcard_required &&
    baseObservationData.client.report_settings.wildcard_active
      ? wildcard.length !== 0
      : true

  const activationService = ObservationActivationService.getInstance()

  if (onAnswerEdit && titleManuallyEdited) {
    // Use the debounced activation service for answer edits
    await activationService.activateObservation(
      Api,
      observationID,
      baseObservationData,
      allowActivate,
    )
    try {
      await Api.patch(`observations/${observationID}`, {
        title: baseObservationData.title,
        is_active: true,
      })
      if (!baseObservationData.is_active && allowActivate)
        await Api.put(`observations/${observationID}/activate/`, {})
    } catch (error) {
      console.error('Error updating observation title:', error)
      setDisableButton(false)
      throw error
    }
    return
  }

  const initialData = flatMap(cloneDeep(memoryState))
  setDisableButton(true)
  const promiseList: Promise<DataWrap<any>>[] = []
  const signaturePromiseList: Promise<DataWrap<any>>[] = []

  const categoriesWithAnswers: number[] = []

  for (const answer of initialData) {
    // Ensure category is treated as a number for consistent comparison
    const categoryNumber = Number(answer?.category)
    if (
      !isNaN(categoryNumber) &&
      !categoriesWithAnswers.includes(categoryNumber)
    ) {
      categoriesWithAnswers.push(categoryNumber)
    }
  }

  if (!baseObservationData.title || (onAnswerEdit && !titleManuallyEdited)) {
    if (categoriesWithAnswers.length === 0) {
      baseObservationData.title =
        'No answers filled - ' +
        moment(
          baseObservationData.date_performed ||
            baseObservationData.date_created,
        ).format('MM/DD/YYYY')
    } else if (categoriesWithAnswers.length > 1) {
      baseObservationData.title =
        baseObservationData.project.name +
        ' - ' +
        moment(
          baseObservationData.date_performed ||
            baseObservationData.date_created,
        ).format('MM/DD/YYYY')
    } else if (categoriesWithAnswers.length === 1) {
      baseObservationData.categories.forEach((cat) => {
        if (cat.id === categoriesWithAnswers[0]) {
          baseObservationData.title =
            cat.name +
            ' - ' +
            moment(
              baseObservationData.date_performed ||
                baseObservationData.date_created,
            ).format('MM/DD/YYYY')
        }
      })
    }
  }

  if (onAnswerEdit) {
    // Use the debounced activation service for answer edits
    await activationService.activateObservation(
      Api,
      observationID,
      baseObservationData,
      allowActivate,
    )
    try {
      await Api.patch(`observations/${observationID}`, {
        title: baseObservationData.title,
        is_active: true,
      })
      if (!baseObservationData.is_active && allowActivate) {
        await Api.put(`observations/${observationID}/activate/`, {})
        baseObservationData.is_active = true
      }
    } catch (error) {
      console.error('Error updating observation in onAnswerEdit:', error)
      setDisableButton(false)
      throw error
    }
  } else {
    try {
      if (wildcardId && wildcard && allowActivate) {
        promiseList.push(
          Api.patch(`observation_wildcards/${wildcardId}`, {
            text: wildcard,
            observation: observationID,
            id: wildcardId,
          }).catch((error) => {
            console.error(`Error patching wildcard ${wildcardId}:`, error)
            throw error
          }),
        )
      } else if (wildcardId && allowActivate) {
        promiseList.push(
          Api.delete(`observation_wildcards/${wildcardId}`, {
            observation: observationID,
            id: wildcardId,
          }).catch((error) => {
            console.error(`Error deleting wildcard ${wildcardId}:`, error)
            throw error
          }) as unknown as Promise<DataWrap<any>>,
        )
      } else {
        if (wildcard && allowActivate) {
          promiseList.push(
            Api.post('observation_wildcards', {
              observation: observationID,
              text: wildcard,
            }).catch((error) => {
              console.error('Error creating wildcard:', error)
              throw error
            }),
          )
        }
      }

      signatures.forEach((signature) => {
        if (signature.deleted) {
          signaturePromiseList.push(
            Api.delete(`signatures/${signature.id}`).catch((error) => {
              console.error(`Error deleting signature ${signature.id}:`, error)
              throw error
            }) as unknown as Promise<DataWrap<any>>,
          )
        } else if (signature.id) {
          // Prepare signature data for API - send image ID and include client_object_key from backend
          const signatureForApi = {
            ...signature,
            image: signature.image?.id,
            printed_name: signature.printed_name, // Include the printed name from user input
            client_object_key: signature.client_object_key, // Use the client_object_key from backend
            fieldmeta: {
              ...signature.fieldmeta,
              printed_name: {
                ...signature.fieldmeta?.printed_name,
                last_updated: new Date().toISOString(),
                last_updated_by_user_id: currentUserId || 0, // Use current user ID parameter
              },
            },
          }
          signaturePromiseList.push(
            Api.patch(`signatures/${signature.id}`, signatureForApi).catch(
              (error) => {
                console.error(
                  `Error patching signature ${signature.id}:`,
                  error,
                )

                // Handle only 403 error for signature patching
                if (
                  (error as { status?: number })?.status === 403 &&
                  Notification
                ) {
                  ;(Notification as { danger: (msg: string) => void }).danger(
                    'You do not have permission to edit this signature. Please contact your administrator.',
                  )
                }
                throw error
              },
            ),
          )
        } else {
          signaturePromiseList.push(
            Api.post('signatures', signature).catch((error) => {
              console.error('Error creating signature:', error)
              throw error
            }),
          )
        }
      })
      notes.forEach((note) => {
        if ((note.deleted && note.id) || (note.text === '' && note.id)) {
          promiseList.push(
            Api.delete(`observation_notes/${note.id}`).catch((error) => {
              console.error(`Error deleting note ${note.id}:`, error)
              throw error
            }) as unknown as Promise<DataWrap<any>>,
          )
        } else if (note.id) {
          promiseList.push(
            Api.patch(`observation_notes/${note.id}`, note).catch((error) => {
              console.error(`Error patching note ${note.id}:`, error)
              throw error
            }),
          )
        } else if (note.deleted) {
          return
        } else if (note.text) {
          promiseList.push(
            Api.post('observation_notes', note).catch((error) => {
              console.error('Error creating note:', error)
              throw error
            }),
          )
        }
      })

      await Promise.all([...promiseList])
      const signatureIdArray: number[] = []
      await Promise.all([...signaturePromiseList]).then((values) => {
        values.forEach((value) => {
          const response = value as ApiResponseData<{ id: number }>
          if (response?.data?.id && typeof response.data.id === 'number') {
            signatureIdArray.push(response.data.id)
          }
        })
      })

      await Api.patch(`observations/${observationID}`, {
        title: baseObservationData.title,
        signatures: signatureIdArray,
        is_active: true,
      }).catch((error) => {
        console.error('Error patching observation:', error)
        throw error
      })

      await Api.put(`observations/${observationID}/synchronised/`, {
        synchronised: true,
      }).catch((error) => {
        console.error('Error setting observation as synchronised:', error)
        throw error
      })

      if (allowActivate) {
        await Api.put(`observations/${observationID}/activate/`, {}).catch(
          (error) => {
            console.error('Error activating observation:', error)
            throw error
          },
        )
      }

      stateService.go('app.observations.list', {
        pageNumber: stateService.params.previousPageNumber,
        search: stateService.params.previousSearch,
        order: stateService.params.previousOrder,
        reverse: stateService.params.previousReverse,
      })
    } catch (error) {
      console.error('Error in sendForm save operation:', error)
      setDisableButton(false)
      throw error
    }
  }
  setDisableButton(false)
}

function listProcessing(
  questions: ReportQuestion[],
  answers: ReportAnswer[],
  observationID: number,
  ANSWER: ReportAnswer,
) {
  const questionsFormData: { [key: string]: ReportAnswer[] } = {}

  // Process answers to create the initial structure
  answers.forEach((answer) => {
    const key = `${answer.question}-${answer.category}`
    if (!questionsFormData[key]) {
      questionsFormData[key] = [
        {
          ...ANSWER,
          observation: observationID,
          question: answer.question,
          category: answer.category,
        },
      ]
    }

    const notes: ProcessingField[] = []
    const corrective_actions: ProcessingField[] = []
    const photos: { id: number; image: string }[] = []

    answer.notes.forEach((note) => {
      if (!note.deleted) {
        const noteWithUser = note as NoteWithUser
        notes.push({
          id: note.id,
          text: note.text,
          deleted: false,
          is_visible: true,
          fieldmeta: {
            text: {
              last_updated:
                answer.date_updated ||
                answer.date_created ||
                new Date().toISOString(),
              last_updated_by_user_id: noteWithUser?.user?.id ?? 0,
            },
          },
        })
      }
    })

    answer.corrective_actions.forEach((corrective_action) => {
      if (!corrective_action.deleted) {
        const actionWithUser = corrective_action as CorrectiveActionWithUser
        corrective_actions.push({
          id: corrective_action.id,
          text: corrective_action.text,
          deleted: false,
          is_visible: true,
          fieldmeta: {
            text: {
              last_updated:
                corrective_action.date_update ||
                corrective_action.date_created ||
                new Date().toISOString(),
              last_updated_by_user_id: actionWithUser?.user?.id ?? 0,
            },
          },
        })
      }
    })

    answer.photos.forEach((photo) => {
      if (!photo.deleted) {
        photos.push({ id: photo.id, image: photo.image })
      }
    })

    questionsFormData[key][answer.copy_sequence_number] = {
      ...ANSWER,
      observation: observationID,
      question: answer.question,
      answer: answer.answer,
      initialAnswer: answer.answer,
      reference: answer.reference,
      party_observed: answer.party_observed,
      severity: answer.severity,
      notes: notes as NoteType[],
      corrective_actions: corrective_actions as ReportCorrectiveAction[],
      photos: photos as ReportPhoto[],
      copy_sequence_number: answer.copy_sequence_number,
      answer_party_observed_answer_list:
        answer.answer_party_observed_answer_list,
      category: answer.category,
    }

    if (answer.id) {
      questionsFormData[key][answer.copy_sequence_number].id = answer.id
    }
  })

  return questionsFormData
}

function signaturesProcessing(signatures: RawSignature[]): SignatureType[] {
  const processedSignatures: SignatureType[] = []
  signatures.forEach((signature) => {
    if (!signature.deleted) {
      // Handle cases where signature might have image data
      const imageData = signature.image || { id: 0, image: signature.url || '' }

      processedSignatures.push({
        id: signature.id,
        image: {
          id: imageData.id,
          url: signature.url || imageData.image,
          external_contact: '',
          user: signature.user as GenericUser,
          image: imageData.image,
          upload_date: signature.date_created,
          deleted: false,
          is_visible: true,
          client_object_key: signature.client_object_key || '',
        },
        printed_name: signature.printed_name || '',
        company_name: '',
        deleted: false,
        client_object_key: signature.client_object_key || '',
        fieldmeta: {
          printed_name: {
            last_updated:
              signature.date_updated ||
              signature.date_created ||
              new Date().toISOString(),
            last_updated_by_user_id:
              (signature.user as { id?: number })?.id || 0, // Use user ID from raw signature
          },
        },
      })
    }
  })
  return processedSignatures
}

function notesProcessing(notes: RawNote[]): NoteType[] {
  const processedNotes: NoteType[] = []
  notes.forEach((note) => {
    if (!note.deleted) {
      processedNotes.push({
        id: note.id,
        text: note.text,
        observation: note.observation,
        deleted: false,
      })
    }
  })
  return processedNotes
}

export function compare(a: { label: string }, b: { label: string }) {
  if (a.label.toLowerCase() < b.label.toLowerCase()) {
    return -1
  }
  if (a.label.toLowerCase() > b.label.toLowerCase()) {
    return 1
  }
  return 0
}

// Create proper interface for the answer parameter in initialSingleAnswer
interface CreateAnswerPayload {
  answer: string
  severity: number
  category: number
  observation: number
  question: number
  id?: number
}

// Fix the field assignment by using a more specific type
export async function processSingleAnswer(
  changedFields: { field: string; value: any }[],
  Api: any,
  Notification: any,
  questionElementId: string,
  setMemoryState: Dispatch<
    SetStateAction<{ [key: string]: ReportAnswer[] } | null>
  >,
  copySequenceNumber: number,
  observationId: any,
) {
  let answer: Partial<ReportAnswer> = {}
  let answers: { [key: string]: ReportAnswer[] } = {}

  setMemoryState((prevState) => {
    if (!prevState) return prevState
    const newState = cloneDeep(prevState)
    changedFields.forEach(({ field, value }) => {
      const answerToUpdate = newState[questionElementId][
        copySequenceNumber
      ] as AnswerWithMetadata
      // Use proper field typing instead of any
      if (field in answerToUpdate) {
        const typedAnswer = answerToUpdate as unknown as Record<string, unknown>
        typedAnswer[field] = value
      }
      const stateWithMeta = newState as {
        edited: boolean
        [key: string]: unknown
      }
      stateWithMeta.edited = true
    })
    newState[questionElementId][copySequenceNumber].edited = true

    answer = newState[questionElementId][copySequenceNumber]
    answers = newState

    return newState
  })

  const answersResponse = await sendAnswers(
    Api as ApiService,
    answers,
    observationId as number,
  )

  setMemoryState((prevState) => {
    if (!prevState) return prevState
    const newState = cloneDeep(prevState)

    const typedResponse = answersResponse as AnswerResponse[]
    typedResponse.forEach((responseAnswer) => {
      const questionKey = `${responseAnswer.question}-${responseAnswer.category}`
      if (newState[questionKey]) {
        newState[questionKey].forEach((answerLink) => {
          if (answerLink) {
            const linkWithMeta = answerLink as AnswerWithMetadata
            linkWithMeta.edited = false
            if (responseAnswer.notes)
              answerLink.notes = combineLocalAndDataVariableFields(
                answerLink.notes as ProcessingField[],
                responseAnswer.notes as ProcessingField[],
              ) as NoteType[]
            if (responseAnswer.corrective_actions) {
              const combinedActions = combineLocalAndDataVariableFields(
                answerLink.corrective_actions as ProcessingField[],
                responseAnswer.corrective_actions as ProcessingField[],
              )
              answerLink.corrective_actions =
                combinedActions as ReportCorrectiveAction[]
            }
          }
        })
      }
    })

    return newState
  })

  if (
    answer.answer === 'cls' &&
    !checkVariableFieldsForText(answer.corrective_actions as ProcessingField[])
  ) {
    return answer.id
  }
  return answer.id
}

const sendAnswers = async (
  Api: ApiService,
  answers: { [key: string]: ReportAnswer[] },
  observationID: number,
) => {
  const promiseList: Promise<DataWrap<any>>[] = []
  const answerList: ReportAnswer[] = []

  flatMap(cloneDeep(answers))
    .filter((answer) => answer !== undefined)
    .forEach((answer) => {
      const answerWithMeta = answer as AnswerWithMetadata
      if (answerWithMeta.edited) {
        if (answer.photos)
          answer.photos.forEach((photo: ReportPhoto) => {
            const photoRef = photo as PhotoReference
            if (photoRef.deleted) {
              promiseList.push(
                Api.delete(`image_upload/${photoRef.id}`) as unknown as Promise<
                  DataWrap<any>
                >,
              )
            }
          })

        const answerToSend: Partial<ReportAnswer> = {
          id: answer.id,
          question: answer.question,
          photos: answer.photos
            .filter((photo: ReportPhoto) => {
              const photoRef = photo as PhotoReference
              return !photoRef.deleted
            })
            .map((photo: ReportPhoto) => {
              const photoRef = photo as PhotoReference
              return photoRef.id
            }) as unknown as ReportPhoto[], // Properly cast to expected interface
          category: answer.category,
          copy_sequence_number: answer.copy_sequence_number,
        }
        answerToSend.reference = answer.reference
        answerToSend.answer = answer.answer
        answerToSend.severity = answer.severity
        answerList.push(answerToSend as ReportAnswer)
      }
    })
  if (!answerList.length) {
    return []
  } else {
    await Promise.all([...promiseList])

    // Process ALL answers, not just the first one
    const savePromises = answerList.map((answer) => {
      const answerWithTimestamp = answer as ReportAnswer & { updatedAt?: Date }
      answerWithTimestamp.updatedAt = new Date()

      return new Promise((resolve, reject) => {
        SyncApi.shared.post(
          {
            ...answer,
            updatedAt: answerWithTimestamp.updatedAt!,
          } as SyncApiAnswer,
          Api,
          observationID,
          answer.copy_sequence_number,
          (response) => {
            resolve(response)
          },
        )
      })
    })

    // Wait for ALL answer saves to complete
    const allAnswerResponses = await Promise.all(savePromises)
    return allAnswerResponses
  }
}

// Fix initialSingleAnswer with proper typing
export async function initialSingleAnswer(
  answer: CreateAnswerPayload,
  Api: ApiService,
  observationID: number,
) {
  const response = await Api.post<{ id: number }[]>(
    `observations/${observationID}/answers`,
    {
      answers: [
        {
          answer: answer.answer,
          deleted: false,
          party_observed: [],
          severity: answer.severity,
          category: answer.category,
          answer_party_observed_answer_list: [],
          copy_sequence_number: 0,
          observation: answer.observation,
          question: answer.question,
          reference: '',
          notes: [],
          corrective_actions: [],
          photos: [],
          party_observed_easy_acces_data: {},
        },
      ],
    },
  )
  const answerWithId = response.data
  const newId = answerWithId[0].id
  if (answer.id !== undefined) {
    answer.id = newId
  }
  return newId
}

const combineLocalAndDataVariableFields = (
  localFields: ProcessingField[],
  responseFields: ProcessingField[],
) => {
  const usedIds: number[] = []
  return localFields
    .filter((field) => !!field)
    .map((field: ProcessingField) => {
      if (field.text === '') return field
      if (field.id) return field
      if (field.deleted) return field
      let fieldToReturn
      responseFields.reverse().forEach((resField) => {
        if (resField.text === field.text && !usedIds.includes(resField.id)) {
          fieldToReturn = resField
          usedIds.push(resField.id)
        }
      })
      return fieldToReturn
    })
}

export const checkVariableFieldsForText = (fields: ProcessingField[]) => {
  let isThereText = false
  for (const field of fields) {
    if (field.text && !field.deleted) {
      isThereText = true
      break
    }
  }
  return isThereText
}

export class SyncApi {
  private publisher = new Subject<{
    answer: SyncApiAnswer
    Api: ApiService
    observationID: number
    copy_sequence_number: number
    date: Date
    resolve: (answer: SyncApiAnswer) => void
  }>()
  private inProgress = new Set<number>()
  static shared = new SyncApi()

  constructor() {
    this.bind()
  }

  post(
    answer: SyncApiAnswer,
    Api: ApiService,
    observationID: number,
    copy_sequence_number: number,
    onFinish: (arg0: SyncApiAnswer) => void,
  ) {
    this.publisher.next({
      answer,
      Api: Api,
      observationID: observationID,
      copy_sequence_number: copy_sequence_number,
      date: new Date(),
      resolve: onFinish,
    })
  }

  print() {
    console.log(this.inProgress)
  }

  private bind() {
    this.publisher
      .pipe(
        RX.filter((el) => {
          const can = !this.inProgress.has(el.answer.id)
          if (can) {
            this.inProgress.add(el.answer.id)
          }
          return can
        }),
        mergeMap((el) => {
          // Save date to auto retry
          const date = el.date
          const resolve = el.resolve
          const Api = el.Api
          const observationID = el.observationID
          const copy_sequence_number = el.copy_sequence_number
          return postRequest(el).pipe(
            tap((response: unknown) => {
              if (hasValidData<Array<{ id: number }>>(response)) {
                el.answer.id = response.data[0].id
              }
            }),
            map((response: unknown): ProcessingElement => {
              if (hasValidData<Array<{ id: number }>>(response)) {
                return {
                  answer: response.data[0] as SyncApiAnswer,
                  Api: Api,
                  observationID: observationID,
                  date: date,
                  copy_sequence_number,
                  resolve: resolve,
                }
              }
              // Fallback to original element structure
              return {
                answer: el.answer,
                Api: Api,
                observationID: observationID,
                date: date,
                copy_sequence_number,
                resolve: resolve,
              }
            }),
          )
        }),
        tap((el: ProcessingElement) => {
          this.inProgress.delete(el.answer.id)
          el.resolve(el.answer)
        }),
      )
      .subscribe(() => {
        // Empty callback
      })
  }
}

function postRequest({
  answer,
  Api,
  observationID,
}: {
  answer: any
  Api: ApiService
  observationID: number
}): Observable<{ data: any }> {
  return from(
    Api.post<any>(`observations/${observationID}/answers`, {
      answers: [answer],
    }),
  )
}
